import { useMemo } from 'react'

import { NestedDropdown } from '@smartbear/swagger-portal-component-library'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { DownloadApiLink } from '~/portal/components/ApiViewer/DownloadApiDropdown/DownloadApiLink'
import { GenerateCodeLink } from '~/portal/components/ApiViewer/GenerateCodeDropdown/GenerateCodeLink'
import { OpenAPI2Definition, OpenAPI3Definition } from '~/portal/types/api.definitions'
import { GENERATOR_SPEC_TYPE, GeneratorLanguages } from '~/portal/types/code.generator'
import { API_DOWNLOAD_FORMATS, API_FILE_TYPES } from '~/portal/types/downloadApi'
import { SHUBApiDetails } from '~/shared/types/api'

const buildPortalSpecDownloadUrl = ({
  productId,
  tableOfContentsId,
  flattened,
  format,
  filename,
}: {
  productId: string,
  tableOfContentsId: string,
  flattened: boolean,
  format: API_FILE_TYPES,
  filename: string,
}): string => {
  const params = new URLSearchParams({
    productId,
    tocId: tableOfContentsId,
    flattened: String(flattened),
    format,
    filename,
  })
  return `/services/api/published-spec-download?${params.toString()}`
}

const buildRegistrySpecDownloadUrl = ({
  orgName,
  name,
  version,
  flattened,
  format,
  filename,
}: {
  orgName: string,
  name: string,
  version: string,
  flattened: boolean,
  format: API_FILE_TYPES,
  filename: string,
}): string => {
  const params = new URLSearchParams({
    orgName,
    name,
    version,
    flattened: String(flattened),
    format,
    filename,
  })
  return `/services/api/registry-spec-download?${params.toString()}`
}

export const GenerateCodeDropdown = ({
  generatorLanguages,
  definition,
  title,
  productId,
  path,
  orgName,
  SHUBApiDetails,
  flattenAvailable = false,
  flattenFlow,
  tableOfContentsId,
  disabled = false,
}: {
  generatorLanguages: GeneratorLanguages,
  definition: OpenAPI2Definition | OpenAPI3Definition,
  title: string,
  productId?: string,
  path?: string,
  orgName?: string,
  SHUBApiDetails?: SHUBApiDetails,
  flattenAvailable?: boolean,
  flattenFlow?: 'portal' | 'registry',
  tableOfContentsId?: string,
  disabled?: boolean,
}) => {
  const { t } = useTranslation()

  const menuItems = useMemo(() => {
    const buildFlattenUrl = (format: API_FILE_TYPES, flattened: boolean): string => {
      const filename = `${title}${flattened ? '-flattened' : '-non-flattened'}`
      return flattenFlow === 'portal'
        ? buildPortalSpecDownloadUrl({ productId: productId!, tableOfContentsId: tableOfContentsId!, flattened, format, filename })
        : buildRegistrySpecDownloadUrl({ orgName: SHUBApiDetails!.orgName!, name: SHUBApiDetails!.name!, version: SHUBApiDetails!.version!, flattened, format, filename })
    }

    return [
      ...(generatorLanguages.clients.length > 0 ? [{
        name: t('components.ui.apiViewer.generateCode.options.clientSDK'),
        options: generatorLanguages.clients.map(el => <GenerateCodeLink key={el} lang={el} type={GENERATOR_SPEC_TYPE.CLIENT} productId={productId} path={path} orgName={orgName} SHUBApiDetails={SHUBApiDetails} />),
      }] : []),
      ...(generatorLanguages.servers.length > 0 ? [{
        name: t('components.ui.apiViewer.generateCode.options.serverStub'),
        options: generatorLanguages.servers.map(el => <GenerateCodeLink key={el} lang={el} type={GENERATOR_SPEC_TYPE.SERVER} productId={productId} path={path} orgName={orgName} SHUBApiDetails={SHUBApiDetails} />),
      }] : []),
      ...(generatorLanguages.documentations.length > 0 ? [{
        name: t('components.ui.apiViewer.generateCode.options.documentation'),
        options: generatorLanguages.documentations.map(el => <GenerateCodeLink key={el} lang={el} type={GENERATOR_SPEC_TYPE.DOCUMENTATION} productId={productId} path={path} orgName={orgName} SHUBApiDetails={SHUBApiDetails} />),
      }] : []),
      {
        name: t('components.ui.apiViewer.downloadApi.label'),
        options: flattenAvailable
          ? API_DOWNLOAD_FORMATS.map(format => ({
            name: format,
            options: [
              <Link
                key={`${format}_flattened`}
                className="block w-full text-left px-4 py-1.5"
                to={buildFlattenUrl(format, true)}
                reloadDocument
              >
                {t('components.ui.apiViewer.downloadApi.flattened')}
              </Link>,
              <Link
                key={`${format}_nonFlattened`}
                className="block w-full text-left px-4 py-1.5"
                to={buildFlattenUrl(format, false)}
                reloadDocument
              >
                {t('components.ui.apiViewer.downloadApi.nonFlattened')}
              </Link>,
            ],
          }))
          : API_DOWNLOAD_FORMATS.map(format => (
            <DownloadApiLink
              key={format}
              label={format}
              format={format}
              title={title}
              definition={definition}
            />
          )),
      },
    ]
  }, [generatorLanguages, definition, title, productId, path, orgName, SHUBApiDetails, flattenAvailable, flattenFlow, tableOfContentsId, t])

  return (
    <NestedDropdown
      menuItems={menuItems}
      dropdownButton={t('components.ui.apiViewer.generateCode.label')}
      disabled={disabled}
    />
  )
}
